/**
 * Transformaciones y mapeos de datos de empleados.
 * Lógica de negocio para convertir datos del backend a formato de UI.
 */
import moment from 'moment';

/**
 * Normaliza sucursales desde la respuesta del API al formato del componente.
 * @param {Array<Object>} lista - Lista cruda de sucursales del API.
 * @returns {Array<Object>} Sucursales con IdSucursal y DescripcionSucursal.
 */
export const mapearSucursales = lista => {
  if (!Array.isArray(lista)) return [];
  return lista.map(sucursal => ({
    ...sucursal,
    IdSucursal: sucursal.sucursal_id || sucursal.IdSucursal,
    DescripcionSucursal:
      sucursal.descripcion_sucursal || sucursal.DescripcionSucursal,
  }));
};

/**
 * Mapea los convenios del usuario al formato del selector de filtros.
 * @param {Array<Object>} listadoConvenios - Convenios del API.
 * @param {Array<Object>} empresasUsuario - Empresas del usuario.
 * @returns {Array<Object>} Convenios listos para el v-select.
 */
export const mapearConvenios = (listadoConvenios, empresasUsuario) => {
  const conveniosPorNit = new Map(
    listadoConvenios.map(convenio => [convenio.nit, convenio])
  );

  return empresasUsuario.flatMap(({ nit }) => {
    const convenio = conveniosPorNit.get(nit);
    if (!convenio?.sucursales) return [];

    return convenio.sucursales.map(sucursal => ({
      tiene_convenio: convenio.tiene_convenio,
      esta_activo: convenio.esta_activo ?? convenio.tiene_convenio,
      muestra_empleados: convenio.muestra_empleados,
      _id: convenio._id,
      empresaId: {
        nitEmpresa: convenio.nit,
        sucursalId: sucursal.sucursal_id,
      },
      razon_social: `${sucursal.descripcion_sucursal}`,
      compania_id: convenio.compania_id,
      descripcion_sucursal: sucursal.descripcion_sucursal,
      plazo_contrato_finalizar: sucursal.plazo_contrato_finalizar,
      plazo_minimo_credito: sucursal.plazo_minimo_credito,
    }));
  });
};

/**
 * Formatea las fechas de los empleados que tienen convenio.
 * @param {Array<Object>} empleados
 * @returns {Array<Object>}
 */
export const mapearEmpleadosConvenio = empleados =>
  empleados.map(empleado => ({
    ...empleado,
    FechaIngreso: empleado.FechaIngreso
      ? moment.utc(empleado.FechaIngreso).format('YYYY-MM-DD')
      : '',
    FechaContratoHasta: empleado.FechaContratoHasta
      ? moment.utc(empleado.FechaContratoHasta).format('YYYY-MM-DD')
      : '',
  }));

/**
 * Mapea un empleado del backend al objeto formData del formulario.
 * @param {Object} empleado - Empleado tal como lo retorna la API.
 * @param {Array<Object>} empresas - Listado de empresas para resolver la compañía por NIT.
 * @returns {Object} Campos del formulario listos para asignar al formData.
 */
export const mapearEmpleadoAFormulario = (empleado, empresas) => ({
  _id: empleado._id,
  nombres: empleado.nombres,
  primerApellido: empleado.primerApellido,
  segundoApellido: empleado.segundoApellido,
  compania: empresas.find(
    empresa => empresa.nit === Number.parseInt(empleado.NitCompañia, 10)
  ),
  sucursal: {
    IdSucursal: empleado.IdSucursal,
    DescripcionSucursal: empleado.DescripcionSucursal,
  },
  tipoDocumento: empleado.TipoDocumento,
  numeroDocumento: empleado.NumeroDocumento,
  tipoContrato: empleado.TipoContrato?._id,
  cargo: empleado.Cargo,
  fechaIngreso: empleado.FechaIngreso,
  fechaContratoHasta: empleado.FechaContratoHasta,
  telefono: empleado.Telefono,
  bloqueado: empleado.Bloqueado,
  estaRetirado: empleado.estaRetirado,
  disponible: empleado.Disponible,
  embargo: empleado.Embargo,
  salario: empleado.Salario,
});

/**
 * Convierte un array de empresas en un Map<nit, empresa> para búsqueda O(1).
 * @param {Array<Object>} empresas
 * @returns {Map<number, Object>}
 */
export const crearMapaEmpresas = empresas =>
  new Map(empresas.map(empresa => [Number(empresa.nit), empresa]));
